/**
 * Collapses a burst of high-frequency samples (pointer moves arrive at up to 240 Hz
 * on some devices) into one delivery per animation frame. Samples are keyed — one
 * slot per pointer — so a two-finger gesture keeps the latest position of *each*
 * finger while intermediate positions are dropped.
 */
export class PointerCoalescer<T> {
  private readonly pending = new Map<number, T>();
  private scheduled = false;

  constructor(
    private readonly schedule: (flush: () => void) => void,
    private readonly deliver: (sample: T) => void,
    private readonly keyOf: (sample: T) => number = () => 0,
  ) {}

  push(sample: T): void {
    this.pending.set(this.keyOf(sample), sample);
    if (this.scheduled) return;
    this.scheduled = true;
    this.schedule(this.flush);
  }

  /** Delivers everything pending immediately (used before pointer-up). */
  readonly flush = (): void => {
    this.scheduled = false;
    if (this.pending.size === 0) return;
    const batch = [...this.pending.values()];
    this.pending.clear();
    for (const sample of batch) this.deliver(sample);
  };

  clear(): void {
    this.pending.clear();
    this.scheduled = false;
  }
}
